import React from 'react';
import { FaCircleMinus, FaUpload } from 'react-icons/fa6';
import { FieldProps } from '@rjsf/utils';
import { getFileIcon } from 'utils/helpers';
import { toast } from 'react-toastify';

const FileUploadWidget: React.FC<FieldProps> = ({ disabled, readonly, uiSchema, onChange }) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [_, setDragCounter] = React.useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [mimeType, setMimeType] = React.useState<string | null>(null);
  const uiSchemaOptions = uiSchema?.['ui:options'] || {};

  const accept = uiSchemaOptions.accept as string;

  const handleFileSelect = async (file: File) => {
    try {
      onChange(file);
      setFileName(file.name);
      setMimeType(file.type);
    } catch (error) {
      console.error('Error reading file:', error);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragging(false);
      }
      return newCounter;
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    setDragCounter(0);

    const file = e.dataTransfer.files?.[0];
    if (!file) {
      return;
    }

    if (!accept.split(',').some((type) => type && file.name.endsWith(type))) {
      toast.error(`Unsupported file type.`);
      return;
    }

    if (!disabled && !readonly) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (!disabled && !readonly && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  return (
    <>
      <div>
        <div
          className={`flex flex-col items-center justify-center p-4 border-2 border-dashed cursor-pointer
                    rounded-lg min-h-60 min-w-60 w-full transition-all duration-200
                    ${
                      disabled || readonly
                        ? 'cursor-not-allowed opacity-50 bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                        : isDragging
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center pointer-events-none">
            <FaUpload
              size={48}
              className={`mb-2 ${isDragging ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}
            />
            <span
              className={`text-center max-w-44 min-h-16 ${
                isDragging ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {isDragging ? 'Drop file here' : 'Drag and drop file here or click to upload'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Supported: {accept.replace(/,/g, ' ')}</span>
          </div>
        </div>
        {fileName && (
          <div className="flex justify-between gap-4 items-center mt-4 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
            <div className="flex items-center gap-2">
              <div className="px-2">{getFileIcon(mimeType ?? '', fileName, 24)}</div>
              <div className="py-2 text-gray-400 font-semibold">{fileName}</div>
            </div>
            <FaCircleMinus
              size={24}
              className="cursor-pointer text-red-500 hover:text-red-600"
              onClick={() => {
                setFileName(null);
                onChange(null);
                setMimeType(null);
              }}
            />
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={accept ?? '*'}
        onChange={handleInputChange}
        disabled={disabled || readonly}
      />
    </>
  );
};

export default FileUploadWidget;
